import { type Instrument, TIMEFRAME_SECONDS, type Timeframe, VENUES } from "@trader/shared/MarketData"
import {
  AttachRunner,
  type AccountSummary,
  type Product,
  PRODUCTS_BY_MARKET,
  productAllowsShort,
  type RunnerDirection,
  type StartPolicy
} from "@trader/shared/Money"
import { parseUserStrategyRef } from "@trader/shared/StrategiesApi"
import { Result, Schema } from "effect"

// "Paper trade…" (docs/design/0005, P3): attaching the chart's strategy to a paper
// account. Plain logic, tested without React: which accounts and products fit the
// instrument, the payload (validated with the API's own schema), and what each
// refusal means to the user.

export const PRODUCT_LABELS: Readonly<Record<Product, string>> = {
  Cash: "Cash (delivery)",
  Intraday: "Intraday (MIS, squared off)",
  Carryforward: "Carryforward (NRML in India; can sell short)"
}

export const START_LABELS: Readonly<Record<StartPolicy, string>> = {
  waitForFlat: "At its next trade (wait until it is flat)",
  adopt: "Now (take its current position)"
}

export const productsFor = (instrument: Instrument): ReadonlyArray<Product> => PRODUCTS_BY_MARKET[VENUES[instrument.venue].market]

/** Accounts that can trade the instrument: open, in its currency. */
export const eligibleAccounts = (accounts: ReadonlyArray<AccountSummary>, instrument: Instrument): ReadonlyArray<AccountSummary> =>
  accounts.filter((account) => account.status !== "Archived" && account.currency === instrument.currency)

/** Intraday and Carryforward can sell short (the server's rule); Cash trades long only. */
export const canShort = productAllowsShort

const timeframeOf = (account: AccountSummary): Timeframe => account.clock.timeframe

export const accountLabel = (account: AccountSummary): string =>
  `${account.name} · ${account.clock._tag} ${timeframeOf(account)} · ${account.currency}`

/**
 * The bars a strategy from this chart runs on (docs/design/0011): the chart's, unless the account
 * steps longer bars (the server refuses a strategy on shorter bars than its account steps).
 */
export const runnerTimeframe = (account: AccountSummary, chart: Timeframe): Timeframe =>
  TIMEFRAME_SECONDS[chart] >= TIMEFRAME_SECONDS[timeframeOf(account)] ? chart : timeframeOf(account)

/** A note when the strategy can't run on the chart's bars: the account steps longer ones. */
export const timeframeNote = (account: AccountSummary | undefined, chart: Timeframe): string | null => {
  if (account === undefined || runnerTimeframe(account, chart) === chart) return null
  return `This account trades ${timeframeOf(account)} bars, so the strategy runs on ${timeframeOf(account)} bars, not the chart's ${chart}.`
}

/** A JSON object copy (drops anything JSON can't carry), as the API stores overrides. */
const jsonObject = (value: Readonly<Record<string, unknown>>): Record<string, unknown> => {
  const copy: unknown = JSON.parse(JSON.stringify(value))
  return typeof copy === "object" && copy !== null && !Array.isArray(copy) ? { ...copy } : {}
}

export interface AttachForm {
  readonly strategyId: string
  readonly instrument: Instrument
  readonly product: Product
  readonly startPolicy: StartPolicy
  readonly direction: RunnerDirection
  /** The pane strategy's overrides (only what differs from the strategy's defaults). */
  readonly inputs: Readonly<Record<string, unknown>>
  readonly properties: Readonly<Record<string, unknown>>
  /** The bars it runs on (default: the account's). */
  readonly timeframe?: Timeframe
}

const decodeAttach = Schema.decodeUnknownResult(AttachRunner)

/**
 * The request, checked with the API's schema. The account currency is always the
 * symbol's (a paper account holds one currency), so a symbol-currency override is
 * dropped and another one refused here, before the server would.
 */
export const attachRequest = (form: AttachForm): Result.Result<AttachRunner, string> => {
  const { currency, ...properties } = jsonObject(form.properties)
  if (currency !== undefined && currency !== form.instrument.currency) {
    return Result.fail(
      `This strategy is set to backtest in ${String(currency)}; paper accounts trade in the symbol's currency (${form.instrument.currency}). Set the account currency back to ${form.instrument.currency} in its properties.`
    )
  }
  const decoded = decodeAttach({
    strategy: form.strategyId,
    instrumentId: form.instrument.id,
    product: form.product,
    inputs: jsonObject(form.inputs),
    properties,
    startPolicy: form.startPolicy,
    direction: form.direction,
    planner: "target-position",
    ...(form.timeframe === undefined ? {} : { timeframe: form.timeframe })
  })
  return Result.isSuccess(decoded) ? Result.succeed(decoded.success) : Result.fail(`This strategy can't be paper traded: ${decoded.failure.message}`)
}

/**
 * What "Paper trade…" attaches for the pane's strategy (docs/design/0005, P5). The
 * server paper trades saved versions only: a catalogue strategy or "user:<id>@<v>"
 * as it is; an editor draft ("draft:<key>") as its saved version when it is that
 * version's source unchanged; otherwise the user must save it first (or may take
 * the saved version instead).
 */
export type PaperTradeTarget =
  | { readonly _tag: "Ready"; readonly strategyId: string; readonly note: string | null }
  | {
    readonly _tag: "Unsaved"
    readonly message: string
    /** The latest saved version, to paper trade instead (null: never saved). */
    readonly saved: { readonly strategyId: string; readonly version: number } | null
  }

const DRAFT = /^draft:([A-Za-z0-9_-]{1,64})$/

/** The draft key of a pane strategy id, or null when it isn't a draft. */
export const draftKeyOfStrategy = (strategyId: string): string | null => DRAFT.exec(strategyId)?.[1] ?? null

export const SAVE_FIRST = "Save the strategy first: paper trading runs a saved version, never an unsaved draft."

/**
 * The target for `strategyId`. For a draft: `draftSource` is its current text (null when
 * none is left: the pane then runs the saved version), `latest` the strategy's newest saved
 * version (null for a strategy never saved).
 */
export const paperTradeTarget = (
  strategyId: string,
  draftSource: string | null,
  latest: { readonly strategyId: string; readonly version: number; readonly source: string } | null
): PaperTradeTarget => {
  const key = draftKeyOfStrategy(strategyId)
  if (key === null) return { _tag: "Ready", strategyId, note: null }
  if (latest === null || parseUserStrategyRef(`user:${latest.strategyId}`) === null) {
    return { _tag: "Unsaved", message: SAVE_FIRST, saved: null }
  }
  const pinned = `user:${latest.strategyId}@${latest.version}`
  if (draftSource === null || draftSource === latest.source) {
    return { _tag: "Ready", strategyId: pinned, note: `It paper trades the saved version ${latest.version}.` }
  }
  return {
    _tag: "Unsaved",
    message: `This draft has changes that aren't saved. ${SAVE_FIRST}`,
    saved: { strategyId: latest.strategyId, version: latest.version }
  }
}

const messageOf = (detail: unknown): string | null =>
  typeof detail === "object" && detail !== null && "message" in detail && typeof detail.message === "string" ? detail.message : null

/** What a refused attach means, from the API's error tag. */
export const attachErrorText = (tag: string, detail: unknown): string => {
  switch (tag) {
    case "RunnerConflict":
      return "A strategy already trades this symbol and product on this account. Stop it first, or pick another product."
    case "ProductDisallowsShort":
      return "The strategy goes short, and this product can't sell short. Trade it long only."
    case "NonDeterministic":
      return "Two runs of the strategy on the same bars disagreed, so it can't be paper traded."
    case "UnknownStrategy":
      return "The server can't find this strategy: it was removed, or it isn't one of yours. Save it, or pick another one."
    case "UnknownInstrument":
      return "The server doesn't know this symbol."
    case "AccountArchived":
      return "This account was reset; pick another one."
    case "AccountNotFound":
      return "This account no longer exists."
    case "AccountBusy":
      return "The account is busy; try again in a moment."
    case "InvalidRunnerParams":
      return messageOf(detail) ?? "The strategy's settings can't be paper traded."
    default:
      return messageOf(detail) ?? `The server refused (${tag}).`
  }
}

/**
 * A warning for orders sized at (nearly) all the equity with margin: the engine
 * refuses such an entry when the next open gaps above the close it was sized at
 * (not enough funds), which the runner can't know when it orders at the close. It
 * then buys and sells back a bar later. A size a little under 100% avoids it.
 */
export const sizingNote = (properties: Readonly<Record<string, unknown>>): string | null => {
  const type = properties["defaultQtyType"]
  const value = Number(properties["defaultQtyValue"])
  const margin = Number(properties["marginLong"] ?? 100)
  return type === "percent_of_equity" && value >= 100 && margin > 0
    ? "Orders are sized at 100% of equity: on a gap up the strategy's entry doesn't fill (not enough funds) while the paper order does, and is sold back a bar later. A size under 100% (say 95%) or a fixed size keeps the paper fills equal to the strategy's."
    : null
}
